import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { UserContext } from '../../context/UserContext';
import { fetchVersionNumber } from '../../api';
import { Home, Menu, X, PiggyBank, LogOut, Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import myPigLogo from './../../assets/Logo/Piggy2.png';
import './MySidebarChild.css';

const useVersion = () => {
  const [version, setVersion] = useState('');

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const appVersion = await fetchVersionNumber();
        setVersion(appVersion);
      } catch (error) {
        console.error('Failed to fetch app version:', error);
      }
    };
    fetchVersion();
  }, []);

  return version;
};

const useMobileDetect = () => {
  const checkIsMobile = useCallback(() => window.innerWidth <= 768, []);
  const [isMobile, setIsMobile] = useState(checkIsMobile);

  useEffect(() => {
    const handleResize = () => setIsMobile(checkIsMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkIsMobile]);

  return isMobile;
};

const useDynamicHeight = () => {
  const ref = useRef(null);

  const updateHeight = useCallback(() => {
    if (ref.current) {
      ref.current.style.height = `${window.innerHeight}px`;
    }
  }, []);

  useEffect(() => {
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [updateHeight]);

  return ref;
};

const NavItem = ({ to, icon: Icon, text, isSelected, onClick }) => (
  <div
    className={`sidebar-nav-item ${isSelected ? 'selected' : ''}`}
    onClick={() => onClick(to)}
  >
    <Icon size={24} />
    <span className="sidebar-nav-text">{text}</span>
  </div>
);

function MySidebarChild({ onToggle, isCollapsed: initialIsCollapsed }) {
  const version = useVersion();
  const { logout } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMobileDetect();
  const sidebarRef = useDynamicHeight();

  const [isCollapsed, setIsCollapsed] = useState(isMobile || initialIsCollapsed);

  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    } else if (isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onToggle(newCollapsedState);
  }, [isCollapsed, onToggle]);

  const handleNavigation = useCallback((to) => {
    navigate(to);
    if (isMobile) {
      setIsCollapsed(true);
      onToggle(true);
    }
  }, [isMobile, navigate, onToggle]);

  const navItems = [
    { to: '/child/dashboard', icon: Home, text: 'Dashboard' },
    { to: '/child/splits', icon: PiggyBank, text: 'Income Splits' },
    { to: '/child/currency-comparison', icon: Globe, text: 'World Currencies' },
  ];

  return (
    <div
      ref={sidebarRef}
      className={`sidebar-container ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
    >
      <div className={`sidebar-content ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-top-section">
          <div className="sidebar-logo">
            <img src={myPigLogo} alt="Logo" className="sidebar-logo-image" />
            <h1 className="sidebar-product-name">Piggy Bunker</h1>
          </div>
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              isSelected={location.pathname === item.to}
              onClick={handleNavigation}
            />
          ))}
        </div>
        <div className="sidebar-bottom-section">
          <div className="sidebar-nav-item" onClick={logout}>
            <LogOut size={24} />
            <span className="sidebar-nav-text">Logout</span>
          </div>
          <p className="sidebar-version">{version}</p>
          <p className="sidebar-version">GenericApp Initiative</p>
        </div>
      </div>
      <button
        className={`sidebar-toggle-button ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
        onClick={toggleSidebar}
      >
        {isCollapsed ? <Menu size={24} /> : <X size={24} />}
      </button>
    </div>
  );
}

export default MySidebarChild;